import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Trash2, Edit2, Check, X, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CountryManager = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCountry, setNewCountry] = useState({
    name: '',
    flag_emoji: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries/`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const handleAddCountry = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/countries/`, newCountry);
      toast.success('Country added successfully');
      setNewCountry({ name: '', flag_emoji: '', display_order: 0, is_active: true });
      setShowAddForm(false);
      fetchCountries();
    } catch (error) {
      console.error('Error adding country:', error);
      toast.error(error.response?.data?.detail || 'Failed to add country');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (country) => {
    setEditingId(country.id);
    setEditingData({
      name: country.name,
      flag_emoji: country.flag_emoji,
      display_order: country.display_order,
      is_active: country.is_active
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingData({});
  };

  const handleSaveEdit = async (countryId) => {
    setLoading(true);
    try {
      await axios.patch(`${API}/countries/${countryId}`, editingData);
      toast.success('Country updated successfully');
      setEditingId(null);
      setEditingData({});
      fetchCountries();
    } catch (error) {
      console.error('Error updating country:', error);
      toast.error(error.response?.data?.detail || 'Failed to update country');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (countryId, countryName) => {
    if (!window.confirm(`Are you sure you want to delete "${countryName}"? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      await axios.delete(`${API}/countries/${countryId}`);
      toast.success('Country deleted successfully');
      fetchCountries();
    } catch (error) {
      console.error('Error deleting country:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete country');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (countryId, currentStatus) => {
    setLoading(true);
    try {
      await axios.patch(`${API}/countries/${countryId}`, {
        is_active: !currentStatus
      });
      toast.success('Country status updated');
      fetchCountries();
    } catch (error) {
      console.error('Error updating country status:', error);
      toast.error('Failed to update country status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-white">Manage Countries</CardTitle>
            <Button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {showAddForm ? 'Cancel' : 'Add Country'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {showAddForm && (
            <form onSubmit={handleAddCountry} className="mb-6 p-4 bg-gray-800 rounded-lg">
              <h3 className="text-white text-lg mb-4">Add New Country</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  placeholder="Country Name"
                  value={newCountry.name}
                  onChange={(e) => setNewCountry({ ...newCountry, name: e.target.value })}
                  required
                  className="bg-gray-700 text-white"
                />
                <Input
                  placeholder="Flag Emoji (e.g., 🇺🇸)"
                  value={newCountry.flag_emoji}
                  onChange={(e) => setNewCountry({ ...newCountry, flag_emoji: e.target.value })}
                  required
                  className="bg-gray-700 text-white"
                />
                <Input
                  type="number"
                  placeholder="Display Order"
                  value={newCountry.display_order}
                  onChange={(e) => setNewCountry({ ...newCountry, display_order: parseInt(e.target.value) || 0 })}
                  className="bg-gray-700 text-white"
                />
                <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                  Add Country
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {countries.map((country) => (
              <div
                key={country.id}
                className="flex items-center justify-between p-4 bg-gray-800 rounded-lg"
              >
                {editingId === country.id ? (
                  <>
                    <div className="flex items-center gap-4 flex-1">
                      <Input
                        value={editingData.name}
                        onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
                        className="bg-gray-700 text-white w-40"
                      />
                      <Input
                        value={editingData.flag_emoji}
                        onChange={(e) => setEditingData({ ...editingData, flag_emoji: e.target.value })}
                        className="bg-gray-700 text-white w-20"
                      />
                      <Input
                        type="number"
                        value={editingData.display_order}
                        onChange={(e) => setEditingData({ ...editingData, display_order: parseInt(e.target.value) || 0 })}
                        className="bg-gray-700 text-white w-24"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleSaveEdit(country.id)}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        disabled={loading}
                        className="bg-gray-600 hover:bg-gray-700"
                        size="sm"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-4 flex-1">
                      <span className="text-2xl">{country.flag_emoji}</span>
                      <span className="text-white font-medium">{country.name}</span>
                      <span className="text-gray-400 text-sm">Order: {country.display_order}</span>
                      <span className={`px-2 py-1 rounded text-xs ${country.is_active ? 'bg-green-600' : 'bg-red-600'} text-white`}>
                        {country.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleToggleActive(country.id, country.is_active)}
                        disabled={loading}
                        className={`${country.is_active ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}
                        size="sm"
                      >
                        {country.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        onClick={() => handleEdit(country)}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(country.id, country.name)}
                        disabled={loading}
                        className="bg-red-600 hover:bg-red-700"
                        size="sm"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {countries.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              No countries found. Add your first country above.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CountryManager;
